import React, { useState, useEffect } from 'react';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import AddressAutocomplete from '../common/AddressAutocomplete';
import localeConfig from '../../config/locale.config';

const VendorDetailsForm = ({ data, onNext, onPrevious, onSave, loading, colors, isPending, registrationStatus }) => {
    const [formData, setFormData] = useState({
        vendor_name: '',
        address: {
            address: '',
            state: '',
            city: '',
            latitude: null,
            longitude: null,
            pincode: ''
        },
        contact_number: '', // This will be set from login mobile number
        gst_number: '',
        pan_number: '',
        // Vendor owner user fields (will be created as VENDOR_OWNER)
        owner_username: '',
        owner_name: '',
        owner_mobile: '', // This will be set from login mobile number
        owner_email: '',
        preferred_language: 'en',
        otp_only_login: true,
        login_enabled: true
    });

    const [addressInput, setAddressInput] = useState('');

    useEffect(() => {
        if (data.vendor) {
            setFormData(prev => ({
                ...prev,
                ...data.vendor,
                // Ensure contact number and owner mobile are set from the data
                contact_number: data.vendor.contact_number || prev.contact_number,
                owner_mobile: data.vendor.owner_mobile || data.vendor.contact_number || prev.owner_mobile
            }));
            
            // Set address input for display
            if (data.vendor.address && typeof data.vendor.address === 'object') {
                setAddressInput(data.vendor.address.address || '');
            } else if (typeof data.vendor.address === 'string') {
                setAddressInput(data.vendor.address);
            }
        }
    }, [data.vendor]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddressSelect = (addressData) => {
        setFormData(prev => ({
            ...prev,
            address: {
                address: addressData.address,
                state: addressData.state,
                city: addressData.city,
                latitude: addressData.latitude,
                longitude: addressData.longitude,
                pincode: addressData.pincode
            }
        }));
        setAddressInput(addressData.address);
    };

    const handleAddressChange = (value) => {
        setAddressInput(value);
        // Update the address field with the input value
        setFormData(prev => ({
            ...prev,
            address: {
                ...prev.address,
                address: value
            }
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Prevent submission if registration is pending
        if (isPending) {
            return;
        }
        
        // Basic validation
        if (!formData.vendor_name || !formData.owner_name || !formData.address.address) {
            alert('Please fill in all required fields');
            return;
        }

        // Generate owner username if not provided
        const ownerUsername = formData.owner_username || formData.owner_name.replace(/\s+/g, '_').toLowerCase() + '_' + Date.now();
        
        onNext({
            vendor: {
                ...formData,
                owner_username: ownerUsername
            }
        });
    };

    const handleSave = () => {
        // Prevent saving if registration is pending
        if (isPending) {
            onSave({});
            return;
        }

        // Basic validation for save
        if (!formData.vendor_name || !formData.owner_name || !formData.address.address) {
            alert('Please fill in all required fields before saving');
            return;
        }

        // Generate owner username if not provided
        const ownerUsername = formData.owner_username || formData.owner_name.replace(/\s+/g, '_').toLowerCase() + '_' + Date.now();
        
        onSave({
            vendor: {
                ...formData,
                owner_username: ownerUsername
            }
        });
    };

    return (
        <div>
            <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Vendor Details</h2>
                <p className="text-sm text-gray-600">Enter your business information and contact details.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Vendor Name */}
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Vendor Name *
                        </label>
                        <input
                            type="text"
                            name="vendor_name"
                            value={formData.vendor_name}
                            onChange={handleInputChange}
                            className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            placeholder="Enter your business name"
                            required
                            disabled={isPending}
                        />
                    </div>

                    {/* Contact Number */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Contact Number *
                        </label>
                        <div className="relative">
                            <input
                                type="tel"
                                name="contact_number"
                                value={formData.contact_number}
                                disabled
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 cursor-not-allowed"
                                placeholder="Contact number"
                            />
                            <div className="absolute inset-y-0 right-0 pr-2 flex items-center">
                                <div className="text-gray-400 hover:text-gray-600 cursor-help" title="Contact number cannot be changed as it's your login number">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* GST Number */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            {localeConfig.businessId.gstLabel}
                        </label>
                        <input
                            type="text"
                            name="gst_number"
                            value={formData.gst_number}
                            onChange={handleInputChange}
                            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            placeholder={localeConfig.businessId.gstPlaceholder}
                        />
                    </div>

                    {/* PAN Number */}
                    {localeConfig.businessId.showPan && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                {localeConfig.businessId.panLabel}
                            </label>
                            <input
                                type="text"
                                name="pan_number"
                                value={formData.pan_number}
                                onChange={handleInputChange}
                                className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                                placeholder={localeConfig.businessId.panPlaceholder}
                                disabled={isPending}
                            />
                        </div>
                    )}

                    {/* Business Address */}
                    <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Business Address *
                        </label>
                        <AddressAutocomplete
                            value={addressInput}
                            onChange={handleAddressChange}
                            onAddressSelect={handleAddressSelect}
                            placeholder="Search for business address"
                            disabled={isPending}
                        />
                    </div>

                    {/* Owner Information Section */}
                    <div className="md:col-span-2">
                        <h4 className="text-sm font-medium text-gray-900 mb-2 border-b pb-1">Owner Information</h4>
                    </div>

                    {/* Owner Name */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Owner Name *
                        </label>
                        <input
                            type="text"
                            name="owner_name"
                            value={formData.owner_name}
                            onChange={handleInputChange}
                            className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            placeholder="Enter owner name"
                            required
                            disabled={isPending}
                        />
                    </div>

                    {/* Owner Username */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Owner Username
                        </label>
                        <input
                            type="text"
                            name="owner_username"
                            value={formData.owner_username}
                            onChange={handleInputChange}
                            className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            placeholder="Auto-generated if left empty"
                            disabled={isPending}
                        />
                    </div>

                    {/* Owner Mobile */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Owner Mobile Number *
                        </label>
                        <div className="relative">
                            <input
                                type="tel"
                                name="owner_mobile"
                                value={formData.owner_mobile}
                                disabled
                                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-gray-50 cursor-not-allowed"
                                placeholder="Owner mobile number"
                            />
                            <div className="absolute inset-y-0 right-0 pr-2 flex items-center">
                                <div className="text-gray-400 hover:text-gray-600 cursor-help" title="Owner mobile number is set to your login number">
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Owner Email */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Owner Email
                        </label>
                        <input
                            type="email"
                            name="owner_email"
                            value={formData.owner_email}
                            onChange={handleInputChange}
                            className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            placeholder="Enter owner email"
                            disabled={isPending}
                        />
                    </div>

                    {/* Preferred Language */}
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Preferred Language
                        </label>
                        <select
                            name="preferred_language"
                            value={formData.preferred_language}
                            onChange={handleInputChange}
                            className={`w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${isPending ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                            disabled={isPending}
                        >
                            <option value="en">English</option>
                            <option value="hi">Hindi</option>
                        </select>
                    </div>
                </div>

                {/* Navigation Buttons */}
                {!isPending && (
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4 border-t">
                        <button
                            type="button"
                            onClick={onPrevious}
                            disabled={loading}
                            className="flex items-center justify-center space-x-2 px-3 py-2 sm:py-1.5 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ArrowLeft className="w-3.5 h-3.5" />
                            <span>Previous</span>
                        </button>

                        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-2">
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={loading}
                                className="flex items-center space-x-2 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <span>Save & Exit</span>
                            </button>

                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center justify-center space-x-2 w-full sm:w-auto px-4 py-2 sm:py-1.5 text-sm text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                style={{
                                    backgroundColor: colors.primary
                                }}
                            >
                                <span>{loading ? 'Saving...' : 'Next'}</span>
                                <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
};

export default VendorDetailsForm; 